/**
 * @fileoverview A redução para a renovação e o cancelamento (31/10/2026, etapa
 * 9 dos planos), no mesmo modal largo da contratação: à esquerda, o que fica
 * (do Pro para o Basic, menos licenças, menos Chat Interno, um pacote de IA
 * menor ou nenhum; no cancelamento, o Starter, com ou sem o Chat Interno); à
 * direita, quanto paga hoje e quanto passa a pagar. Nada é cobrado: até a
 * renovação (no cancelamento, até o fim do ciclo pago), a Empresa fica com o
 * que tem. Se algo que ela usa não cabe no plano novo, a tela diz o quê, e a
 * escolha do que fica vem depois de confirmar (até a renovação).
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, IconButton, MenuItem } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Check, X } from "lucide-react";

import ModernSelect from "../../ModernSelect";
import { RadioGroup, Stepper, billingErrorText, contractItemLabel } from "../PlanControls";
import { InfoTip } from "../../../pages/Flows/components/TriageManager/InfoTip";
import toastError from "../../../errors/toastError";
import api from "../../../services/api";
import { i18n } from "../../../translate/i18n";
import { contractItems, describeTerms, money, thousands } from "../../../utils/billingPlans";
import { formatUtcDate } from "../../../utils/formatLocaleDate";
import useStyles from "../PlansDialog/styles";

const t = (key, options) => i18n.t(`reductionDialog.${key}`, options);
const tp = (key, options) => i18n.t(`plansDialog.${key}`, options);

const ReductionDialog = ({ open, onClose, overview, onDone, onChoose, mode = "reduce" }) => {
  const classes = useStyles();
  const { catalog, usage } = overview;
  const canceling = mode === "cancel";
  // O que a Empresa tem hoje (a redução parte daqui e só diminui).
  const current = {
    plan: overview.plan,
    seats: overview.terms?.seatsContracted ?? 0,
    internalChatSeats: overview.terms?.internalChatSeats ?? 0,
    aiPackMessages: overview.terms?.aiPackMessages ?? null,
  };
  const starterOnly = current.plan === "starter";
  // No cancelamento, dá para ficar com o Chat Interno no Starter (até o máximo de pessoas dele).
  const starterPlan = catalog.plans.find((item) => item.key === "starter");
  const keepMax = Math.min(starterPlan?.maxUsers || 2, current.internalChatSeats);
  const [keepInternal, setKeepInternal] = useState(false);
  const [keepSeats, setKeepSeats] = useState(1);

  const [plan, setPlan] = useState(current.plan);
  const definition = catalog.plans.find((item) => item.key === plan) || catalog.plans[1];
  // Nunca acima do contrato: se a Empresa usa mais do que contratou (exceção do super), o servidor diz o que não cabe.
  const contractSeats = Math.max(current.seats, definition.minBilledUsers);
  const minSeats = Math.min(Math.max(definition.minBilledUsers, usage.users), contractSeats);
  const maxSeats = contractSeats;
  const [seats, setSeats] = useState(current.seats);
  const minInternal = Math.min(Math.max(starterOnly ? 1 : 0, usage.internalChatUsers), current.internalChatSeats);
  const maxInternal = Math.max(starterOnly ? current.internalChatSeats : Math.min(current.internalChatSeats, seats), minInternal);
  const [internalChatSeats, setInternalChatSeats] = useState(current.internalChatSeats);
  const [aiPackMessages, setAiPackMessages] = useState(current.aiPackMessages || "");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteNonce, setQuoteNonce] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [scheduled, setScheduled] = useState(null);
  const stateTitle = useRef(null);

  // Cada abertura começa do que a Empresa tem.
  useEffect(() => {
    if (!open) return;
    setPlan(current.plan);
    setSeats(current.seats);
    setInternalChatSeats(current.internalChatSeats);
    setAiPackMessages(current.aiPackMessages || "");
    setKeepInternal(false);
    setKeepSeats(Math.max(1, Math.min(keepMax, usage.internalChatUsers || 1)));
    setQuote(null);
    setQuoteError(null);
    setScheduled(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // As licenças entre o mínimo e o que a Empresa tem; o Chat Interno, nunca acima delas.
  useEffect(() => {
    setSeats((value) => Math.min(Math.max(value, minSeats), maxSeats));
  }, [minSeats, maxSeats]);
  useEffect(() => {
    setInternalChatSeats((value) => Math.min(Math.max(value, minInternal), maxInternal));
  }, [minInternal, maxInternal]);

  useEffect(() => {
    if (scheduled && stateTitle.current) stateTitle.current.focus();
  }, [scheduled]);

  const terms = canceling
    ? { plan: "starter", seats: null, internalChatSeats: keepInternal ? keepSeats : 0, aiPackMessages: null }
    : starterOnly
      ? { plan: "starter", seats: null, internalChatSeats, aiPackMessages: null }
      : { plan, seats, internalChatSeats, aiPackMessages: aiPackMessages === "" ? null : Number(aiPackMessages) };
  const unchanged =
    !canceling &&
    terms.plan === current.plan &&
    (terms.seats ?? 0) === (current.seats ?? 0) &&
    terms.internalChatSeats === current.internalChatSeats &&
    (terms.aiPackMessages ?? null) === (current.aiPackMessages ?? null);
  const items = useMemo(() => contractItems(catalog, terms), [catalog, terms.plan, terms.seats, terms.internalChatSeats, terms.aiPackMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  // A cotação da redução (o servidor diz o valor novo e o que não caberia no plano menor).
  const termsKey = JSON.stringify(terms);
  useEffect(() => {
    if (!open || scheduled) return undefined;
    if (unchanged) {
      setQuote(null);
      setQuoteError(null);
      setQuoting(false);
      return undefined;
    }
    let alive = true;
    setQuoting(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/billing/reductions/quote", JSON.parse(termsKey));
        if (alive) {
          setQuote(data.quote);
          setQuoteError(null);
        }
      } catch (err) {
        if (alive) {
          setQuote(null);
          setQuoteError(err?.response?.data?.error || "network");
        }
      } finally {
        if (alive) setQuoting(false);
      }
    }, 350);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [open, scheduled, termsKey, unchanged, quoteNonce]);

  const blockers = quote?.blockers || [];

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/reductions", terms);
      setScheduled(data.reduction);
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "ERR_BILLING_QUOTE_CHANGED") setQuoteNonce((value) => value + 1);
      else toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const paidPlans = catalog.plans.filter((item) => item.key !== "starter");
  const packs = catalog.aiPacks.filter((pack) => pack.messages <= (current.aiPackMessages ?? 0));

  const cancellation = quote?.kind === "cancellation";
  const currentName = catalog.plans.find((item) => item.key === current.plan)?.name;

  const renderCancelChoices = () => (
    <>
      <div className={classes.group}>
        <span className={classes.groupTitle}>{t("cancel.whatHappens")}</span>
        <p className={classes.methodNote}>{starterOnly ? t("cancel.textInternal") : t("cancel.text", { plan: currentName })}</p>
        {!starterOnly && (
          <p className={classes.methodNote}>
            {t("cancel.starterLimits", {
              users: starterPlan.maxUsers,
              accounts: starterPlan.accounts,
              whatsapps: starterPlan.whatsapps,
              boards: starterPlan.crmBoards,
              common: thousands(starterPlan.commonMessages),
              ai: thousands(starterPlan.aiMessages),
            })}
          </p>
        )}
      </div>
      {!starterOnly && keepMax > 0 && (
        <div className={classes.group}>
          <label className={classes.keepRow}>
            <input type="checkbox" className={classes.keepBox} checked={keepInternal} onChange={(event) => setKeepInternal(event.target.checked)} />
            <span>
              <span className={classes.groupTitle}>{t("cancel.keepInternal")}</span>
              <span className={classes.keepHint}>{t("cancel.keepInternalHint", { price: money(catalog.internalChatPriceCents) })}</span>
            </span>
          </label>
          {keepInternal && (
            <Stepper
              classes={classes}
              name={tp("internal")}
              hint={tp("perSeat", { price: money(catalog.internalChatPriceCents) })}
              help={t("cancel.keepHelp", { max: keepMax })}
              value={keepSeats}
              min={1}
              max={keepMax}
              onChange={setKeepSeats}
            />
          )}
        </div>
      )}
    </>
  );

  const renderForm = () => (
    <div className={classes.body}>
      <div className={classes.choices}>
        {canceling && renderCancelChoices()}
        {!canceling && !starterOnly && current.plan === "pro" && (
          <div className={classes.group}>
            <span className={classes.groupHead}>
              <span className={classes.groupTitle} id="reduction-dialog-plan">
                {tp("plan")}
              </span>
              <InfoTip text={t("planInfo")} />
            </span>
            <RadioGroup
              className={classes.planOptions}
              labelledBy="reduction-dialog-plan"
              options={paidPlans.map((item) => ({ value: item.key, item }))}
              value={plan}
              onChange={setPlan}
              renderOption={({ value, item }, props) => (
                <button type="button" className={`${classes.planOption} ${value === plan ? classes.planOptionOn : ""}`} {...props}>
                  <span className={classes.planName}>
                    <span>
                      {item.name}
                      {value === current.plan && <span className={classes.planCurrent}>{tp("planCurrent")}</span>}
                    </span>
                    <span className={`${classes.radioDot} ${value === plan ? classes.radioDotOn : ""}`} aria-hidden="true" />
                  </span>
                  <span className={classes.planPrice}>{tp("perSeat", { price: money(item.pricePerUserCents) })}</span>
                </button>
              )}
            />
          </div>
        )}

        {!canceling && !starterOnly && (
          <Stepper
            classes={classes}
            name={tp("seats")}
            hint={tp("perSeat", { price: money(definition.pricePerUserCents) })}
            help={t("seatsHelp", { min: minSeats })}
            value={seats}
            min={minSeats}
            max={maxSeats}
            onChange={setSeats}
          />
        )}

        {!canceling && (current.internalChatSeats > 0 || starterOnly) && (
          <Stepper
            classes={classes}
            name={tp("internal")}
            hint={tp("perSeat", { price: money(catalog.internalChatPriceCents) })}
            help={starterOnly ? t("internalHelpStarter") : t("internalHelp", { min: minInternal })}
            value={internalChatSeats}
            min={minInternal}
            max={maxInternal}
            onChange={setInternalChatSeats}
          />
        )}

        {!canceling && !starterOnly && definition.aiPacks && current.aiPackMessages && (
          <div className={classes.group}>
            <span className={classes.groupTitle} id="reduction-dialog-pack">
              {tp("aiPack")}
            </span>
            <ModernSelect
              size="small"
              value={aiPackMessages}
              onChange={(event) => setAiPackMessages(event.target.value)}
              labelId="reduction-dialog-pack"
              displayEmpty
              MenuProps={{ disableScrollLock: true }}
              formControlProps={{ fullWidth: true }}
            >
              <MenuItem value="">{tp("aiPackNone", { ai: thousands(definition.aiMessages) })}</MenuItem>
              {packs.map((pack) => (
                <MenuItem key={pack.messages} value={pack.messages}>
                  {tp("aiPackOption", { messages: thousands(pack.messages), price: money(pack.priceCents) })}
                  {current.aiPackMessages === pack.messages ? ` ${tp("packCurrent")}` : ""}
                </MenuItem>
              ))}
            </ModernSelect>
          </div>
        )}
      </div>

      <aside className={classes.summary} aria-labelledby="reduction-dialog-summary">
        <h3 className={classes.summaryTitle} id="reduction-dialog-summary">
          {tp("summaryTitleChange")}
        </h3>
        <ul className={classes.summaryLines}>
          {items.map((item) => (
            <li key={item.code} className={classes.summaryLine}>
              <span>{contractItemLabel(item)}</span>
              <span className={classes.summaryLineValue}>{money(item.quantity * item.unitCents)}</span>
            </li>
          ))}
        </ul>
        <div className={`${classes.quoteBlock} ${quoting && quote ? classes.quoteBusy : ""}`} aria-live="polite" aria-busy={quoting}>
          {overview.subscription && <p className={classes.quoteToday}>{tp("quoteToday", { amount: money(quote ? quote.currentMonthlyCents : overview.subscription.monthlyCents) })}</p>}
          {quote ? (
            <div className={classes.totalRow}>
              <span className={classes.totalLabel}>{cancellation ? t("cancel.until", { date: formatUtcDate(quote.effectiveAt) }) : t("from", { date: formatUtcDate(quote.effectiveAt) })}</span>
              <span className={classes.totalValue}>{money(quote.nextMonthlyCents)}</span>
            </div>
          ) : quoting ? (
            <div className={`${classes.totalRow} ${classes.quoteWaiting}`}>
              <CircularProgress size={16} color="inherit" aria-label={tp("quoting")} />
              <span className={classes.totalLabel}>{tp("quoting")}</span>
            </div>
          ) : quoteError ? (
            <div className={classes.quoteProblem} role="alert">
              <p className={classes.formError}>{billingErrorText(quoteError)}</p>
              <button type="button" className={classes.noticeAction} onClick={() => setQuoteNonce((value) => value + 1)}>
                {tp("quoteRetry")}
              </button>
            </div>
          ) : (
            <p className={`${classes.totalRow} ${classes.totalLabel}`}>{t("nothing")}</p>
          )}
        </div>
        {blockers.length > 0 && (
          <div className={classes.quoteProblem}>
            <p className={classes.notice}>{t("blockersTitle")}</p>
            {blockers.map((blocker) => (
              <p key={blocker.resource} className={classes.methodNote}>
                {t(`blockers.${blocker.resource}`, { used: blocker.used, limit: blocker.limit })}
              </p>
            ))}
            <p className={classes.methodNote}>{t("blockersHint")}</p>
          </div>
        )}
        <span className={classes.spacer} />
        <p className={classes.methodNote}>{cancellation ? t("cancel.note") : t("note")}</p>
        <button type="button" className={classes.primary} onClick={submit} disabled={submitting || quoting || !quote}>
          {submitting && <CircularProgress size={16} color="inherit" />}
          {quote || quoteError ? (cancellation ? t("cancel.submit") : t("submit")) : quoting ? tp("quoting") : t("nothing")}
        </button>
      </aside>
    </div>
  );

  const renderDone = () => (
    <div className={classes.state} role="status">
      <span className={`${classes.stateIcon} ${classes.stateOk}`}>
        <Check />
      </span>
      <h3 className={classes.stateTitle} ref={stateTitle} tabIndex={-1}>
        {scheduled.kind === "cancellation" ? (starterOnly ? t("cancel.doneTitleInternal") : t("cancel.doneTitle")) : t("doneTitle")}
      </h3>
      <p className={classes.stateText}>
        {scheduled.kind === "cancellation"
          ? starterOnly
            ? t("cancel.doneTextInternal", { date: formatUtcDate(scheduled.effectiveAt) })
            : t("cancel.doneText", { plan: currentName, date: formatUtcDate(scheduled.effectiveAt) })
          : t("doneText", { date: formatUtcDate(scheduled.effectiveAt), what: describeTerms(catalog, scheduled.changes), amount: money(scheduled.amountCents) })}
      </p>
      {scheduled.needsChoice && <p className={classes.stateHint}>{t("chooseNow", { date: formatUtcDate(scheduled.effectiveAt) })}</p>}
      <div className={classes.stateActions}>
        {scheduled.needsChoice && onChoose ? (
          <>
            <button type="button" className={`${classes.primary} ${classes.primaryInline}`} onClick={onChoose}>
              {t("chooseAction")}
            </button>
            <button type="button" className={classes.secondary} onClick={onDone}>
              {t("chooseLater")}
            </button>
          </>
        ) : (
          <button type="button" className={`${classes.primary} ${classes.primaryInline}`} onClick={onDone}>
            {tp("close")}
          </button>
        )}
      </div>
    </div>
  );

  const close = () => (scheduled ? onDone() : onClose());

  return (
    <Dialog open={open} onClose={submitting ? undefined : close} maxWidth={false} classes={{ paper: classes.dialogPaper }} aria-labelledby="reduction-dialog-title">
      <div className={classes.dialogHead}>
        <div>
          <h2 className={classes.dialogTitle} id="reduction-dialog-title">
            {canceling ? (starterOnly ? t("cancel.titleInternal") : t("cancel.title", { plan: currentName })) : t("title")}
          </h2>
          {!scheduled && <p className={classes.dialogText}>{canceling ? t("cancel.subtitle") : t("subtitle")}</p>}
        </div>
        <IconButton size="small" className={classes.dialogClose} onClick={close} disabled={submitting} aria-label={tp("close")}>
          <X size={18} />
        </IconButton>
      </div>
      {scheduled ? renderDone() : renderForm()}
    </Dialog>
  );
};

export default ReductionDialog;
